import { DestroyRef, Directive, ElementRef, inject } from '@angular/core';
import { SystemService } from 'libs/shared/src';
import { HeaderService } from '../services/header.service';

/**
 * Tracks the rendered height of the host element (the collapsible topbar wrapper) and publishes
 * it to {@link HeaderService.topbarHeight}. The sticky header offsets its `top` by this value
 * (negatively) so the topbar scrolls out of view while the navbar stays pinned to the top.
 *
 * Shared by the basic and slim header templates so both behave identically. Resets the height to
 * `0` on teardown so a stale, non-zero value can never leave the next header pushed down.
 */
@Directive({
  selector: '[gsTopbarHeight]',
  standalone: true
})
export class TopbarHeightDirective {
  // Services
  private readonly destroyRef = inject(DestroyRef);
  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly headerService = inject(HeaderService);
  private readonly system = inject(SystemService);

  constructor() {
    // ResizeObserver is browser-only; on the server (or when unsupported) leave the height at 0.
    if (!this.system.browser || typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      this.headerService.topbarHeight.set(Math.round(entries[0]?.contentRect.height ?? 0));
    });
    observer.observe(this.el.nativeElement);

    this.destroyRef.onDestroy(() => {
      observer.disconnect();
      this.headerService.topbarHeight.set(0);
    });
  }
}
